'use client';

import { pushABTesting } from '@/api/analytics';
import { User } from '@/api/auth';
import { CmsABTest } from '@/api/cms/abTests';
import { getSessionId } from '@/helpers/analytics';
import { Country } from '@/helpers/conf';
import { timestampByUTC } from '@/helpers/dateTime';
import { Locale } from '@/helpers/languages';
import { groupMember } from '@/helpers/user';
import { useLocalStorageStore } from '@/store';

export const getRandomNumber = (from: number, to: number) =>
  Number((Math.random() * (to - from) + from).toFixed(3));

type ABTestingVersion<T> = {
  name: string;
  probability?: number;
  component?: React.ComponentType<T> | null;
  isVersionToApply?: (user: User) => boolean;
};

export type ABTestingConfig<T> = {
  authorized?: boolean;
  platform?: 'mobile' | 'desktop';
  isMobileDevice?: boolean;
  stags?: string[];
  countries?: Country[];
  locale?: Locale[];
  userGroup?: string;
  defaultVersion: React.ComponentType<T> | string | null;
  versions: ABTestingVersion<T>[];
  turnOffTest?: boolean;
};

type ABTestingVersionRequired<T> = Omit<ABTestingVersion<T>, 'probability'> & {
  probability: number;
};

const getRandomVersion = <T,>(versions: ABTestingVersionRequired<T>[]) => {
  const randomNumber = getRandomNumber(1, 100);

  const ascendingProbability = (
    versionA: ABTestingVersionRequired<T>,
    versionB: ABTestingVersionRequired<T>
  ) => versionA.probability - versionB.probability;

  const transformProbability = (
    version: ABTestingVersionRequired<T>,
    index: number,
    versions: ABTestingVersionRequired<T>[]
  ) => {
    if (index === 0) return version;

    const probability = versions
      .slice(0, index + 1)
      .reduce((sum, { probability }) => sum + probability, 0);

    return {
      ...version,
      probability: versions.length === index + 1 ? 100 : probability,
    };
  };

  const appropriateByRandomNumber = ({
    probability,
  }: ABTestingVersionRequired<T>) => probability >= randomNumber;

  return versions
    .sort(ascendingProbability)
    .map(transformProbability)
    .find(appropriateByRandomNumber);
};

const getTestStorageName = (experiment: string) => `ab-testing__${experiment}`;

const ABTestingStoreValue = (experiment: string) =>
  useLocalStorageStore
    .getState()
    .get<{ name: string } | null>(getTestStorageName(experiment), null);

const cmsABTestCheck = <T extends object>(
  experiment: string,
  versions: ABTestingVersion<T>[],
  cmsABTests?: CmsABTest[]
) => {
  const versionsNames = versions.map(({ name }) => name);

  const cmsABTestVersion = cmsABTests?.find(
    ({ title }) => title === experiment
  )?.version;

  if (cmsABTestVersion && versionsNames.includes(cmsABTestVersion)) {
    const currentVersionComponent = versions.find(
      ({ name }) => name === cmsABTestVersion
    )?.component;

    return currentVersionComponent ?? cmsABTestVersion;
  }
};

export const getABTestVersion = <T extends object>(
  experiment: string,
  versions: ABTestingVersion<T>[],
  cmsABTests?: CmsABTest[]
) => {
  const cmsABTestVersion = cmsABTestCheck(experiment, versions, cmsABTests);

  if (cmsABTestVersion) return cmsABTestVersion;

  const ABTestingExperimentStore = ABTestingStoreValue(experiment)?.name;

  return ABTestingExperimentStore;
};

export const abTestingFunction = <T extends object>(
  experiment: string,
  config: ABTestingConfig<T>,
  user?: User,
  countryCode?: string,
  cmsABTests?: CmsABTest[],
  isMobileDevice?: boolean
) => {
  if (typeof window === 'undefined') return null;
  if (config?.turnOffTest) return null;

  const cmsABTestVersion = cmsABTestCheck<T>(
    experiment,
    config.versions,
    cmsABTests
  );

  if (cmsABTestVersion) return cmsABTestVersion;

  const savedABTestingName = ABTestingStoreValue(experiment)?.name;

  const savedExperiment = config.versions.find(
    ({ name }) => name === savedABTestingName
  );

  if (savedExperiment != null)
    return savedExperiment.component !== undefined
      ? savedExperiment.component
      : savedExperiment.name;

  const byUserGroup = (() => {
    if (config.userGroup == null) return true;

    if (user?.statuses == null) return false;

    return groupMember(config.userGroup)(user);
  })();

  const byAuthorized = (() => {
    if (config.authorized == null) return true;

    if (config.authorized && user?.id != null) return true;

    if (!config.authorized && user?.id == null) return true;

    return false;
  })();

  const byPlatform = (() => {
    if (!config.platform) return true;

    if (config.platform === 'mobile' && isMobileDevice) return true;

    if (config.platform === 'desktop' && !isMobileDevice) return true;

    return false;
  })();

  const byStag = (() => {
    if (config.stags == null) return true;

    const storageStag = useLocalStorageStore
      .getState()
      .get<string | null>('stag', null);

    if (storageStag == null) return false;

    return config.stags.includes(storageStag);
  })();

  const byCountry = (() => {
    if (config.countries == null) return true;

    return config.countries.includes(countryCode as Country);
  })();

  const conditions =
    byAuthorized && byUserGroup && byStag && byCountry && byPlatform;

  if (conditions) {
    const version = (() => {
      const configVersion = config.versions.find(
        ({ isVersionToApply }) => isVersionToApply && isVersionToApply(user!)
      );
      if (configVersion) return configVersion;

      const empty = config.versions.find(
        ({ probability }) => probability === undefined
      );
      if (empty) return;

      return getRandomVersion(config.versions as ABTestingVersionRequired<T>[]);
    })();

    if (version == null) return config.defaultVersion;

    useLocalStorageStore
      .getState()
      .replace(getTestStorageName(experiment), { name: version.name });

    pushABTesting({
      experiment,
      version: version.name,
      attributes: {
        sessionId: getSessionId(),
        userId: user?.id,
        createdAt: timestampByUTC(),
        updatedAt: timestampByUTC(),
      },
    });

    return version.component !== undefined ? version.component : version.name;
  }

  return config.defaultVersion;
};
